/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject } from '@angular/core';
import { catchError, first, map, Observable, of, forkJoin } from 'rxjs';
import { DATABASE_NAME } from 'src/app/config/app.config';
import { STORE_DATABASE } from 'src/app/core/enums/storage/store-database.enum';
import { Logger } from 'src/app/core/interfaces/Logger';
import { IndexedDBRepository } from 'src/app/shared/interface/indexed-db-storage.model';
import { Injectable } from '@angular/core';
import { Session } from '../../domain/model/session.model';
import { ISessionStorage } from '../../domain/model/session.interface';
import { ISessionStorageRepository } from '../interfaces/session-storage.repository';
import { SessionMapper } from '../mapper/session.mapper';

@Injectable({
  providedIn: 'root',
})
export class SessionStorageRepository
  extends IndexedDBRepository<Session>
  implements ISessionStorageRepository
{
  readonly dbName = DATABASE_NAME;
  readonly instanceName = STORE_DATABASE.session;
  private readonly logger = inject(Logger);

  constructor() {
    super();
  }

  /**
   * Checks if a value is valid (not null, undefined, or empty object)
   */
  private isValidValue(value: any): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    
    // Check if it's an empty object
    if (typeof value === 'object' && Object.keys(value).length === 0) {
      return false;
    }
    
    return true;
  }



  get(): Observable<Session> {
    return this.getItems().pipe(
      first(),
      map((items: any[]) => {
        const sessionStorage: ISessionStorage = items.reduce(
          (accumulator: ISessionStorage, item: any) => {
            if (item === null) {
              return accumulator;
            }

            // Skip items without type
            if (!item?.type) {
              return accumulator;
            }

            const type = item.type as keyof ISessionStorage;


            // Ensure the accumulator has all required properties initialized  
            if (!accumulator[type]) {
              accumulator[type] = item;
            } else {
              this.logger.error(
                new Error(
                  `[SessionStorageRepository][get] Duplicate type found: ${item.type}`
                ),
                item
              );
            }

            return accumulator;
          },
          {} as ISessionStorage
        );

        return SessionMapper.fromStorage(sessionStorage);
      }),
      catchError((error) => {
        this.logger.error(
          new Error('[SessionStorageRepository][get] Error fetching session'),
          error
        );

        return of(SessionMapper.toEmpty());
      })
    );
  }

  /**
   * Validates if a type is a valid session storage property
   */
  private isValidSessionStorageType(type: string): type is keyof ISessionStorage {
    const validTypes: (keyof ISessionStorage)[] = [
      'drawer',
      'audit',
      'folder',
      'location',
      'terminal',
      'waiter',
    ];
    
    return validTypes.includes(type as keyof ISessionStorage);
  }

  set(session: Session): Observable<void> {
    const sessionStorage: ISessionStorage = SessionMapper.toStorage(session);
    const entries = Object.entries(sessionStorage);

    const validEntries = entries.filter(([, value]) => this.isValidValue(value));
    const invalidKeys = entries
      .filter(([, value]) => !this.isValidValue(value))
      .map(([key]) => key);

    const saveOperations = validEntries.map(([key, value]) =>
      this.setItem(key, { ...value, type: key })
    );

    const removeOperations = invalidKeys.length
      ? this.removeItems(invalidKeys)
      : of(void 0);

    const allOperations = [
      ...(saveOperations.length ? [forkJoin(saveOperations)] : []),
      removeOperations
    ].filter(op => op !== of(void 0));

    return (allOperations.length ? forkJoin(allOperations) : of([])).pipe(
      first(),
      map(() => void 0),
      catchError((error) => {
        this.logger.error(
          new Error('[SessionStorageRepository][set] Error saving session'),
          error
        );

        return of(void 0);
      })
    );
  }

  clear(): Observable<void> {
    return this.clearAll().pipe(
      first(),
      map(() => void 0)
    );
  }
}
